import { useEffect, useRef, useCallback } from 'react';
import toast, { Toaster, useToaster } from 'react-hot-toast';

const TOAST_DURATION_MS = parseInt(import.meta.env.VITE_TOAST_DURATION_MS || '10000', 10);

function ToastVisibilityManager() {
  const { toasts } = useToaster();
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  const schedule = useCallback((id: string) => {
    if (document.hidden || timers.current.has(id)) return;
    const t = setTimeout(() => {
      toast.dismiss(id);
      timers.current.delete(id);
    }, TOAST_DURATION_MS);
    timers.current.set(id, t);
  }, []);

  useEffect(() => {
    const activeIds = new Set(toasts.map(t => t.id));

    toasts.forEach(t => {
      if (t.type !== 'loading') schedule(t.id);
    });

    timers.current.forEach((timer, id) => {
      if (!activeIds.has(id)) {
        clearTimeout(timer);
        timers.current.delete(id);
      }
    });
  }, [toasts, schedule]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        timers.current.forEach(clearTimeout);
        timers.current.clear();
      } else {
        toastsRef.current.forEach(t => {
          if (t.type !== 'loading') schedule(t.id);
        });
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [schedule]);

  return null;
}

export function SmartToaster() {
  return (
    <>
      <ToastVisibilityManager />
      <Toaster
        position="bottom-right"
        containerStyle={{ zIndex: 10000 }}
        toastOptions={{
          duration: Infinity,
          style: {
            background: '#1e293b',
            color: '#e2e8f0',
            border: '1px solid #334155',
          },
        }}
      />
    </>
  );
}
